import { io, Socket } from "socket.io-client";
import { Tank } from "./Tank";
import * as THREE from "three";
import { NETWORK_CONFIG } from "./config";
import { TrainingTrackManager } from "./training/ModularTrainingTrackManager";
import { TrainingTrackService } from "./training/TrainingTrackService";
import { BotManager } from "./BotManager";
import { Bot } from "./Bot";
import { TankValidation } from "./utils/TankValidation";
import { EventManager } from "./EventManager";
import { showInactivityOverlay, updateRoundTimer, updateScoreboard } from "./hud";

// Deklaracja dla TypeScript, aby crosshairPosition było dostępne globalnie
declare global {
  interface Window {
    socket?: any;
    playerTank?: Tank;
    gameState?: {
      tanks: Tank[];
      scene?: THREE.Scene;
      environmentService?: {
        getRandomRespawnPoint: () => {
          position: THREE.Vector3;
          rotation: number;
        } | null;
      };
      trainingTrackManager?: TrainingTrackManager;
      trainingTrackService?: TrainingTrackService;
      Bot?: typeof Bot;
      botManager?: BotManager;
    };
    tanks?: Tank[];
    remoteTanks?: Tank[];
    showDamageIndicator?: (damage: number) => void;
    crosshairPosition: THREE.Vector3;
    updateHUDHealth?: (health: number) => void;
    currentPing?: number; // Dodajemy informację o aktualnym pingu
  }
}

// Rozszerzamy interfejs Socket aby obsługiwał playerTank
declare module "socket.io-client" {
  interface Socket {
    playerTank?: Tank;
  }
}

let socket: Socket;
const tanks = new Map<string, Tank>();
let scene: THREE.Scene;

const debugSocket = (socket: any) => {
  console.log("Socket debug info:", {
    connected: socket.connected,
    disconnected: socket.disconnected,
    events: Object.keys(socket._callbacks || {}),
    url: socket.io.uri,
    protocol: window.location.protocol,
    host: window.location.host,
    origin: window.location.origin,
    serverUrl: NETWORK_CONFIG.SERVER_URL,
  });

  // Add a listener for connect/disconnect events
  socket.on("connect", () => {
    console.log("Socket connected");
  });

  socket.on("disconnect", () => {
    console.log("Socket disconnected");
  });

};

// Funkcja do regularnego mierzenia pingu
let pingInterval: number | null = null;
let lastPingTime = 0;

function startPingMeasurement(socket: Socket) {
  // Zatrzymaj poprzedni interwał, jeśli istnieje
  if (pingInterval) {
    clearInterval(pingInterval);
  }
  window.currentPing = undefined;

  // Nasłuchuj na odpowiedź pong z serwera
  socket.off("pong");
  socket.on("pong", (data: { clientTime: number; serverTime: number }) => {
    const currentTime = Date.now();
    const pingValue = currentTime - data.clientTime;

    // Zapisz wartość pingu w globalnej zmiennej
    window.currentPing = pingValue;
    console.log(`Ping: ${pingValue}ms`);
  });

  // Wysyłaj ping co 5 sekund zamiast co 2 sekundy, aby zmniejszyć obciążenie serwera
  socket.once("connect", () => {
    lastPingTime = Date.now();
    socket.emit("ping", { time: lastPingTime });
  });

  pingInterval = window.setInterval(() => {
    lastPingTime = Date.now();
    socket.emit("ping", { time: lastPingTime });
  }, NETWORK_CONFIG.PING_UPDATES) as unknown as number;
}

export function setupSocket(playerTank: Tank | null, gameScene: THREE.Scene, username?: string): Socket {
  // Wyczyść istniejące połączenie, jeśli istnieje
  if (socket) {
    console.log("Closing existing socket connection");
    socket.disconnect();
  }

  console.log("Setting up socket connection to:", NETWORK_CONFIG.SERVER_URL);

  // Konfiguracja Socket.IO
  socket = io(NETWORK_CONFIG.SERVER_URL, {
    transports: ["websocket", "polling"],
    upgrade: true,
    rememberUpgrade: true,
    secure: true,
    rejectUnauthorized: false,
    reconnection: true,
    reconnectionAttempts: NETWORK_CONFIG.RECONNECTION_ATTEMPTS,
    reconnectionDelay: NETWORK_CONFIG.RECONNECTION_DELAY,
    timeout: NETWORK_CONFIG.TIMEOUT,
    autoConnect: true,
    forceNew: true,
  });

  scene = gameScene;

  //DEBUG
  debugSocket(socket);

  // Start ping measurement
  startPingMeasurement(socket);

  // Logowanie stanu połączenia
  socket.on("connect", () => {
    console.log("[Network] Connected to server with socket id:", socket.id);
    // Wyślij playerId do serwera
    if (playerTank) {
      console.log("Initializing player tank:", {
        playerId: playerTank.playerId,
        username: username || "Anonymous",
      });

      socket.emit("playerInit", {
        playerId: playerTank.playerId,
        username: username, // Pass the username to the server
      });

      // Nasłuchuj na potwierdzenie inicjalizacji
      socket.once("playerInitialized", (data: { playerId: string }) => {
        if (data.playerId === playerTank.playerId) {
          console.log("Player initialized:", playerTank.playerId);
          playerTank.setInitialized(true);
        }
      });
    }
  });

  socket.on("disconnect", (reason) => {
    console.log("[Network] Disconnected:", reason);
  });

  socket.on("kickedForInactivity", (data: { message: string; inactiveMinutes: number; timeoutMs: number }) => {
    console.warn("[Network] Kicked for inactivity", data);
    showInactivityOverlay(data);

    if (socket.playerTank) {
      socket.playerTank.stopMovingNetwork();
      socket.playerTank.setInitialized(false);
    }

    EventManager.removeAllListeners("network");
  });

  socket.on("reconnect", (attemptNumber) => {
    console.log("[Network] Reconnected after", attemptNumber, "attempts");
    // Ponownie wyślij playerId po reconnect
    if (playerTank) {
      socket.emit("playerInit", { playerId: playerTank.playerId });
    }
  });

  socket.on("reconnect_attempt", (attemptNumber) => {
    console.log("[Network] Reconnection attempt:", attemptNumber);
  });

  socket.on("connect_error", (error) => {
    console.error("Socket connection error:", error);
    console.error("Connection details:", {
      url: NETWORK_CONFIG.SERVER_URL,
      protocol: window.location.protocol,
      host: window.location.host,
      transportOptions: socket.io.opts.transports,
      secure: socket.io.opts.secure,
    });
  });

  socket.on("serverRestart", (data) => {
    console.warn("Server is restarting:", data.message);
    // Display a notification to the user that the server is restarting
    alert("Server is restarting. You will be disconnected.");
  });

  socket.on("scoreboard:update", (payload) => {
    updateScoreboard(payload);
  });

  socket.on("roundTimer:update", (payload) => {
    updateRoundTimer(payload);
  });

  socket.on("error", (error) => {
    console.error("Socket error:", error);
  });

  socket.on("playerLeft", (playerId: string) => {
    console.log(`Player ${playerId} left the game`);
    const tank = tanks.get(playerId);
    if (tank) {
      tank.dispose(); // Call dispose before removing from scene
      scene.remove(tank.mesh);
      tanks.delete(playerId);
    }
  });

  socket.on("initialPlayers", (players: { [playerId: string]: any }) => {
    console.log("=== Initial Players Debug ===");
    console.log("Received players:", players);
    console.log("Current tanks in map:", Array.from(tanks.keys()));
    console.log("Scene reference exists:", !!scene);

    console.log("FULL PLAYERS DATA RECEIVED:", players);
    console.log("MY PLAYER ID:", playerTank?.playerId);
    console.log("TANK MAP STATE BEFORE:", Array.from(tanks.keys()));

    for (const playerId in players) {
      console.log(`Processing player ${playerId}:`, {
        alreadyExists: tanks.has(playerId),
        playerData: players[playerId],
      });

      // Sprawdź, czy to lokalny gracz i czy już mamy czołg dla tego gracza
      if (socket.playerTank && socket.playerTank.playerId === playerId) {
        console.log(`Using existing tank for local player: ${playerId}`);

        // Dodaj istniejący czołg do mapy tanks jeśli go tam nie ma
        if (!tanks.has(playerId)) {
          tanks.set(playerId, socket.playerTank);
        }
        continue; // Przejdź do następnego gracza
      }

      // Sprawdź czy już istnieje czołg dla tego gracza
      if (tanks.has(playerId)) {
        console.log(`Tank already exists for player: ${playerId}`);
        continue; // Już mamy czołg dla tego gracza, przejdź dalej
      }

      //if (!tanks.has(playerId) && playerTank?.playerId !== playerId) {
      // Nie twórz czołgu jeśli to jest lokalny gracz lub jeśli już istnieje w mapie
      if (!tanks.has(playerId) && socket.id !== playerId && playerTank?.playerId !== playerId) {
        try {
          console.log(`Creating tank for player ${playerId}`);

          // Check if this is a bot based on ID prefix or isBot flag
          const isBot = playerId.startsWith("bot-") || players[playerId]?.isBot === true;
          console.log(`Player ${playerId} is bot: ${isBot}`);

          let tank;

          if (isBot) {
            const BotClass = window.gameState?.Bot;
            if (BotClass) {
              const existingTankIndex = window.gameState?.tanks?.findIndex((t) => t.playerId === playerId) ?? -1;
              let existingTank = undefined;
              if (existingTankIndex !== -1 && window.gameState?.tanks) {
                existingTank = window.gameState.tanks[existingTankIndex];
              }

              const botInstance = new BotClass(scene, existingTank);
              botInstance.tank.playerId = playerId;
              tank = botInstance.tank;

              if (!tank.mesh.parent) {
                scene.add(tank.mesh);
              }

              if (window.gameState?.botManager) {
                window.gameState.botManager.addExistingBot(botInstance);
              }

              console.log(`Created bot instance for ${playerId}`);
            } else {
              tank = new Tank(scene);
              tank.playerId = playerId;
              console.log(`Created tank for bot ${playerId} (Bot class not available)`);
            }
          } else {
            // Nie twórz czołgu dla lokalnego gracza
            // For regular players, create a normal tank
            tank = new Tank(scene);
            tank.playerId = playerId;
            // Dodaj mesh tylko jeśli nie ma jeszcze rodzica
            if (!tank.mesh.parent) {
              scene.add(tank.mesh); //add mesh for real player
            }
          }

          // Log tank creation details
          console.log("New tank details:", {
            playerId: tank.playerId,
            isBot: isBot,
            position: players[playerId]?.position,
            sceneChildren: scene.children.length,
          });

          const position = players[playerId]?.position || { x: 0, y: 0, z: 0 };
          tank.mesh.position.set(position.x, position.y, position.z);

          tank.mesh.rotation.y = players[playerId]?.rotation || 0;
          if (tank.turret) {
            tank.turret.rotation.y = players[playerId]?.turretRotation || 0;
          }

          // Dodaj czołg do mapy czołgów
          tanks.set(playerId, tank);
          if (!tank.mesh.parent) {
            scene.add(tank.mesh);
          }

          // Update username if available
          if (players[playerId].username) {
            tank.setUsername(players[playerId].username);
          }

          // Dodaj czołg do środowiska
          if (socket.playerTank && socket.playerTank.environment) {
            console.log("Adding remote tank to environment:", {
              playerId: playerId,
              environmentTanksCount: socket.playerTank.environment.tanks.length,
            });
            tank.setEnvironment(socket.playerTank.environment);
          } else {
            console.warn("Cannot add tank to environment - no player tank or environment:", {
              hasPlayerTank: !!socket.playerTank,
              hasEnvironment: !!socket.playerTank?.environment,
            });
          }

          // After creating each remote tank:
          if (window.remoteTanks) {
            window.remoteTanks.push(tank);
          }
          if (window.gameState) {
            window.gameState.tanks.push(tank);
          }

          // Validate tank meshes after adding new tank
          //TankValidation.validateTankMeshes(scene, tanks);
          // Clean up any orphaned tank meshes
          //TankValidation.cleanupOrphanedTankMeshes(scene, tanks);

          // Debug tank meshes
          debugTankMeshes(scene);

          // Verify tank was added correctly
          console.log("Tank added verification:", {
            isInTanksMap: tanks.has(playerId),
            isInScene: scene.children.includes(tank.mesh),
            isInEnvironment: socket.playerTank?.environment?.tanks.includes(tank) || false,
            totalTanks: tanks.size,
            tankPosition: tank.mesh.position.toArray(),
          });

          console.log("SCENE CHILDREN:", scene.children.length);
          console.log("IS TANK MESH IN SCENE:", scene.children.includes(tank.mesh));
        } catch (error) {
          console.error(`Error creating tank for player ${playerId}:`, error);
        }
      }

      console.log("TANK MAP STATE AFTER:", Array.from(tanks.keys()));
    }

    // Final state check
    console.log("=== Final State ===");
    console.log("Total tanks in map:", tanks.size);
    console.log("Tank IDs:", Array.from(tanks.keys()));
    console.log("Scene children count:", scene.children.length);
    console.log("Environment tanks count:", socket.playerTank?.environment?.tanks.length || 0);
  });

  socket.on("newPlayer", (playerId: string, playerData: any) => {
    console.log("New player joined:", {
      playerId,
      playerData,
      existingTanks: Array.from(tanks.keys()),
      isBot: playerId.startsWith("bot-") || playerData?.isBot === true,
    });

    //if (!tanks.has(playerId)) {
    //if (!tanks.has(playerId) && playerTank?.playerId !== playerId) {
    // Nie twórz czołgu jeśli to jest lokalny gracz lub jeśli już istnieje w mapie
    if (!tanks.has(playerId) && socket.id !== playerId && playerTank?.playerId !== playerId) {
      console.log("Creating tank for new player:", playerId);

      try {
        // Check if this is a bot based on ID prefix or isBot flag
        const isBot = playerId.startsWith("bot-") || playerData?.isBot === true;
        console.log(`Player ${playerId} is bot: ${isBot}`);

        let tank;

        if (isBot) {
          const BotClass = window.gameState?.Bot;
          if (BotClass) {
            const existingTankIndex = window.gameState?.tanks?.findIndex((t) => t.playerId === playerId) ?? -1;
            let existingTank = undefined;
            if (existingTankIndex !== -1 && window.gameState?.tanks) {
              existingTank = window.gameState.tanks[existingTankIndex];
            }

            const botInstance = new BotClass(scene, existingTank);
            botInstance.tank.playerId = playerId;
            tank = botInstance.tank;

            if (!tank.mesh.parent) {
              scene.add(tank.mesh);
            }

            if (window.gameState?.botManager) {
              window.gameState.botManager.addExistingBot(botInstance);
            }

            console.log(`Created bot instance for ${playerId}`);
          } else {
            // Fallback if Bot class not available
            tank = new Tank(scene);
            tank.playerId = playerId;
            console.log(`Created tank for bot ${playerId} (Bot class not available)`);
          }
        } else {
          // For regular players, create a normal tank
          tank = new Tank(scene);
          tank.playerId = playerId; // Explicitly set the playerId for remote tank
        }

        // Dodaj czołg do mapy czołgów i sceny przed wywołaniem spawn
        tanks.set(playerId, tank);
        if (!tank.mesh.parent) {
          scene.add(tank.mesh);
        }

        // Log tank creation details
        console.log("New tank details:", {
          playerId: tank.playerId,
          isBot: isBot,
          position: playerData?.position,
          sceneChildren: scene.children.length,
        });

        // Ustaw pozycję i rotację jeśli są dostępne
        if (playerData?.position) {
          tank.mesh.position.set(playerData.position.x, playerData.position.y, playerData.position.z);
        }

        if (playerData?.rotation !== undefined) {
          tank.mesh.rotation.y = playerData.rotation;
        }

        if (playerData?.turretRotation !== undefined && tank.turret) {
          tank.turret.rotation.y = playerData.turretRotation;
        }

        // Dodaj czołg do środowiska
        if (socket.playerTank?.environment) {
          tank.setEnvironment(socket.playerTank.environment);
        }

        // Dodaj do globalnych list
        if (window.remoteTanks) {
          window.remoteTanks.push(tank);
        }
        if (window.gameState) {
          window.gameState.tanks.push(tank);
        }

        console.log("Tank added successfully:", {
          playerId,
          isBot: isBot,
          isInTanksMap: tanks.has(playerId),
          isInScene: scene.children.includes(tank.mesh),
          isInEnvironment: socket.playerTank?.environment?.tanks.includes(tank),
          totalTanks: tanks.size,
        });

        console.log("SCENE CHILDREN:", scene.children.length);
        console.log("IS TANK MESH IN SCENE:", scene.children.includes(tank.mesh));
      } catch (error) {
        console.error("Error creating tank for new player:", error);
      }
    } else {
      console.log("Tank already exists for player:", playerId);
    }
  });

  socket.on(
    "tankCollision",
    (data: {
      tank1Id: string; // To jest playerId pierwszego czołgu
      tank2Id: string; // To jest playerId drugiego czołgu
      damage: number;
      tank1Health: number;
      tank2Health: number;
      collisionPoint: { x: number; y: number; z: number };
    }) => {
      // Update health for both tanks involved in collision
      const tank1 = tanks.get(data.tank1Id);
      const tank2 = tanks.get(data.tank2Id);

      if (tank1) {
        tank1.health = data.tank1Health;
        console.log("Updated tank1 health:", {
          playerId: data.tank1Id,
          oldHealth: tank1.health,
          newHealth: data.tank1Health,
        });
      } else {
        console.warn("Tank1 not found for collision:", data.tank1Id);
      }

      if (tank2) {
        tank2.health = data.tank2Health;
        console.log("Updated tank2 health:", {
          playerId: data.tank2Id,
          oldHealth: tank2.health,
          newHealth: data.tank2Health,
        });
      } else {
        console.warn("Tank2 not found for collision:", data.tank2Id);
      }

      console.log("Received tank collision:", {
        tank1PlayerId: data.tank1Id,
        tank2PlayerId: data.tank2Id,
        damage: data.damage,
        tank1Health: data.tank1Health,
        tank2Health: data.tank2Health,
        collisionPoint: data.collisionPoint,
      });
    }
  );

  socket.on(
    "tankUpdate",
    (data: {
      playerId: string; // Używamy playerId zamiast tankId
      position: { x: number; y: number; z: number };
      rotation: number;
      turretRotation: number;
      timestamp: number;
      hp: number;
    }) => {
      const tank = tanks.get(data.playerId); // Używamy playerId
      if (!tank) {
        console.warn("Received position update for unknown tank:", data.playerId);
        return;
      }

      // Update tank's health if provided
      if (data.hp !== undefined) {
        tank.health = data.hp;
      }

      const currentPos = tank.mesh.position.clone();
      const newPos = new THREE.Vector3(data.position.x, data.position.y, data.position.z);
      const positionDelta = currentPos.distanceTo(newPos);

      tank.mesh.position.set(data.position.x, data.position.y, data.position.z);
      tank.mesh.rotation.y = data.rotation;
      if (tank.turret) {
        tank.turret.rotation.y = data.turretRotation;
      }

      /*
    console.log('Received tank update:', {
      tankId: data.tankId,
      position: [data.position.x, data.position.y, data.position.z],
      rotation: data.rotation,
      turretRotation: data.turretRotation,
      timestamp: data.timestamp,
      latency: Date.now() - data.timestamp
    });*/
    }
  );

  // Define the interface for tank damage event data
  interface TankDamageData {
    targetId: string; // playerId (UUID) of the damaged tank
    damage: number;
    health: number;
    projectilePosition: { x: number; y: number; z: number };
    fromServer?: boolean;
    isDead?: boolean; // Optional flag for tank destruction
  }

  // Add new event handler for tank damage
  socket.on("tankDamage", (data: TankDamageData) => {
    //console.log("[Network] Received tankDamage event:", data);

    // Znajdź czołg do którego aplikujemy obrażenia
    const tank = findTankById(data.targetId);
    if (!tank) {
      console.warn("[Network] Tank not found for damage:", data.targetId);
      return;
    }

    const isLocalTank = tank.playerId === window.playerTank?.playerId;
    const oldHealth = tank.health;

    /*
    console.log("[Network] Found tank to apply damage to:", {
      playerId: tank.playerId,
      oldHealth,
      newHealth: data.health,
      damage: data.damage,
      isSelf: isLocalTank
    });
    */

    // Dla lokalnego czołgu, zniszczenie jest obsługiwane w game.ts CZY TO JEST OK?
    if (isLocalTank) {
      return;
    }

    // Dla zdalnego czołgu, aplikujemy obrażenia
    tank.health = data.health;
  });

  // Obsługa zniszczenia czołgu
  socket.on("tankDestroyed", (data: { playerId: string }) => {
    /*
    console.log("[Network] Event propagation:", {
      event: "tankDestroyed",
      playerId: data.playerId,
      gameInstance: !!window.gameState,
      tankFound: !!findTankById(data.playerId),
      tankState: findTankById(data.playerId)
        ? {
            isDestroyed: findTankById(data.playerId)?.isDestroyed,
            health: findTankById(data.playerId)?.health,
            position: findTankById(data.playerId)?.mesh.position.toArray(),
          }
        : null,
    });
    */

    //console.log("[Network] Received tankDestroyed event:", data);
    //console.log("[Network] Destroying tank with playerId:", data.playerId);

    const tankToDestroy = findTankById(data.playerId);
    if (!tankToDestroy) {
      console.warn("[Network] Tank not found for destruction:", data.playerId);
      return;
    }

    // Sprawdź czy to lokalny czołg używając playerId
    const isLocalTank = tankToDestroy.playerId === window.playerTank?.playerId;
    //console.log("[Network] Is local player tank:", isLocalTank);

    /*
    console.log("[Network] Found tank to destroy:", data.playerId, "current state:", {
      isDestroyed: tankToDestroy.isDestroyed,
      health: tankToDestroy.health,
      position: tankToDestroy.mesh.position.toArray(),
    });
    */

    // Dla lokalnego czołgu, zniszczenie jest już obsłużone lokalnie CZY TO JEST OK?
    if (isLocalTank) {
      console.log(`[Network] Local tank destruction is handled locally: ${data.playerId}`);
      return;
    }

    // Dla zdalnych czołgów, wywołaj destroy()
    //console.log("[Network] Destroying remote tank:", data.playerId);
    tankToDestroy.destroy();
  });

  // Obsługa respawnu czołgu
  socket.on("tankRespawned", (data: { playerId: string; health: number }) => {
    /*
    console.log("[Network] Event propagation:", {
      event: "tankRespawned",
      playerId: data.playerId,
      gameInstance: !!window.gameState,
      tankFound: !!findTankById(data.playerId),
      tankState: findTankById(data.playerId)
        ? {
            isDestroyed: findTankById(data.playerId)?.isDestroyed,
            health: findTankById(data.playerId)?.health,
            position: findTankById(data.playerId)?.mesh.position.toArray(),
          }
        : null,
    });
    */

    //console.log("[Network] Received tankRespawned event:", data);

    if (!window.gameState) {
      console.error("[Network] gameState is not initialized!");
      return;
    }

    // Sprawdź czy to lokalny czołg używając playerId
    const isLocalTank = data.playerId === window.playerTank?.playerId;
    if (isLocalTank) {
      //console.log("[Network] Ignoring respawn event for local tank - it handles its own respawn");
      return;
    }

    const tankToRespawn = findTankById(data.playerId);
    if (tankToRespawn) {
      /*
      console.log(`[Network] Found tank to respawn: ${data.playerId}, current state:`, {
        isDestroyed: tankToRespawn.isDestroyed,
        health: tankToRespawn.health,
        position: tankToRespawn.mesh.position.toArray()
      });
      */

      // Aktualizuj zdrowie czołgu
      tankToRespawn.health = data.health;

      // Respawnuj czołg
      tankToRespawn.spawn();
      /*
      console.log("[Network] Tank respawned successfully:", {
        playerId: data.playerId,
        health: tankToRespawn.health,
        position: tankToRespawn.mesh.position.toArray()
      });
      */
    } else {
      console.warn("[Network] Tank not found for respawn:", data.playerId);
    }
  });

  // Wysyłanie aktualizacji pozycji czołgu
  function sendTankUpdate(tank: Tank) {
    if (!socket || !tank.isInitialized()) {
      return;
    }

    const updateData = {
      playerId: tank.playerId,
      position: {
        x: tank.mesh.position.x,
        y: tank.mesh.position.y,
        z: tank.mesh.position.z,
      },
      rotation: tank.mesh.rotation.y,
      turretRotation: tank.turret?.rotation.y || 0,
      timestamp: Date.now(),
    };

    socket.emit("tankUpdate", updateData);
  }

  // Handle keyboard input
  const keyState: { [key: string]: boolean } = {};

  function updateTankMovement() {
    if (!socket.playerTank || !socket.playerTank.isInitialized()) {
      return; // Nie wykonuj operacji na niezainicjalizowanym czołgu
    }

    // Forward/Backward movement
    if (keyState["w"]) {
      socket.playerTank.startMovingForwardNetwork();
    } else if (keyState["s"]) {
      socket.playerTank.startMovingBackwardNetwork();
    } else {
      socket.playerTank.stopMovingNetwork();
    }

    // Tank rotation
    if (keyState["a"]) {
      socket.playerTank.startTurningLeftNetwork();
    } else if (keyState["d"]) {
      socket.playerTank.startTurningRightNetwork();
    }

    // Turret rotation
    if (keyState["q"]) {
      socket.playerTank.turretTurnDirection = -1;
    } else if (keyState["e"]) {
      socket.playerTank.turretTurnDirection = 1;
    } else {
      socket.playerTank.turretTurnDirection = 0;
    }

    // Shooting
    if (keyState[" "]) {
      // Sprawdź czy mamy pozycję celownika
      if (!window.crosshairPosition) {
        console.warn("No crosshair position available");
        return;
      }

      // Send shot event to server
      socket.emit("tankShot", {
        position: socket.playerTank.mesh.position.toArray(),
        direction: window.crosshairPosition.toArray(),
        timestamp: Date.now(),
      });

      socket.playerTank.shoot(scene);
    }
  }

  // Tymczasowo wyłączamy obsługę klawiatury w module network.ts
  EventManager.addListener("network", document, "keydown", ((event: KeyboardEvent) => {
    keyState[event.key.toLowerCase()] = true;
    updateTankMovement();
  }) as EventListener);

  EventManager.addListener("network", document, "keyup", ((event: KeyboardEvent) => {
    keyState[event.key.toLowerCase()] = false;
    updateTankMovement();
  }) as EventListener);

  return socket;
}

function findTankById(playerId: string) {
  return window.gameState?.tanks.find((t) => t.playerId === playerId);
}

function debugTankMeshes(scene: THREE.Scene) {
  const tankMeshes = scene.children.filter((child) => child.userData && child.userData.type === "tank");
  console.log(`Tank meshes in scene: ${tankMeshes.length}`);
  tankMeshes.forEach((mesh) => {
    console.log(`- ${mesh.uuid} (${mesh.userData?.tank?.playerId || "unknown"})`);
  });
}
